import {
  QuickEffectIds,
  QuickEffectDirectionConsts,
} from '@packages/systems/ix2/shared-constants';

import {
  ArrowLeft12 as ArrowLeft,
  ArrowRight12 as ArrowRight,
  ArrowBottom12 as ArrowBottom,
  ArrowTop12 as ArrowTop,
  ArrowTopLeft12 as ArrowTopLeft,
  ArrowTopRight12 as ArrowTopRight,
  ArrowBottomLeft12 as ArrowBottomLeft,
  ArrowBottomRight12 as ArrowBottomRight,
  ArrowClockwise12 as ArrowClockwise,
  ArrowCounterClockwise12 as ArrowCounterClockwise,
} from '@packages/systems/ui/icons';

const {
  FADE_EFFECT,
  SLIDE_EFFECT,
  GROW_EFFECT,
  SHRINK_EFFECT,
  SPIN_EFFECT,
  FLY_EFFECT,
  POP_EFFECT,
  FLIP_EFFECT,
  JIGGLE_EFFECT,
  PULSE_EFFECT,
  DROP_EFFECT,
  BLINK_EFFECT,
  BOUNCE_EFFECT,
  FLIP_LEFT_TO_RIGHT_EFFECT,
  FLIP_RIGHT_TO_LEFT_EFFECT,
  RUBBER_BAND_EFFECT,
  JELLO_EFFECT,
  GROW_BIG_EFFECT,
  SHRINK_BIG_EFFECT,
  PLUGIN_LOTTIE_EFFECT,
} = QuickEffectIds;

const {
  LEFT,
  RIGHT,
  BOTTOM,
  TOP,
  BOTTOM_LEFT,
  BOTTOM_RIGHT,
  TOP_RIGHT,
  TOP_LEFT,
  CLOCKWISE,
  COUNTER_CLOCKWISE,
} = QuickEffectDirectionConsts;

const PATH_ICONS = {
  [LEFT]: ArrowLeft,
  [RIGHT]: ArrowRight,
  [BOTTOM]: ArrowBottom,
  [TOP]: ArrowTop,
} as const;

const CORNER_ICONS = {
  [TOP_LEFT]: ArrowTopLeft,
  [TOP_RIGHT]: ArrowTopRight,
  [BOTTOM_LEFT]: ArrowBottomLeft,
  [BOTTOM_RIGHT]: ArrowBottomRight,
} as const;

const ROTATE_ICONS = {
  [CLOCKWISE]: ArrowClockwise,
  [COUNTER_CLOCKWISE]: ArrowCounterClockwise,
} as const;

const REVERSE_ARROW_LABEL_MAP = {
  [LEFT]: RIGHT,
  [RIGHT]: LEFT,
  [TOP]: BOTTOM,
  [BOTTOM]: TOP,
  [TOP_LEFT]: BOTTOM_RIGHT,
  [TOP_RIGHT]: BOTTOM_LEFT,
  [BOTTOM_LEFT]: TOP_RIGHT,
  [BOTTOM_RIGHT]: TOP_LEFT,
  // to appease the flow gods
  [CLOCKWISE]: CLOCKWISE,
  [COUNTER_CLOCKWISE]: COUNTER_CLOCKWISE,
} as const;

// @ts-expect-error - TS7006 - Parameter 'map' implicitly has an 'any' type.
const getReverseDirection = (map) => {
  const result: Record<string, any> = {};
  for (const direction in map) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly LEFT: "RIGHT"; readonly RIGHT: "LEFT"; readonly TOP: "BOTTOM"; readonly BOTTOM: "TOP"; readonly TOP_LEFT: "BOTTOM_RIGHT"; readonly TOP_RIGHT: "BOTTOM_LEFT"; readonly BOTTOM_LEFT: "TOP_RIGHT"; readonly BOTTOM_RIGHT: "TOP_LEFT"; readonly CLOCKWISE: "CLOCKWISE"; readonly COUNTER_CLOCKWISE: "COUNTER_CLOCKWISE...'.
    result[REVERSE_ARROW_LABEL_MAP[direction]] = map[direction];
  }
  return result;
};

const QuickEffectCategory = {
  appear: 'appear' as const,
  emphasis: 'emphasis' as const,
  plugin: 'plugin' as const,
} as const;

export const QuickEffectConsts = {
  [QuickEffectCategory.plugin]: [
    {
      header: true,
      id: QuickEffectCategory.plugin,
      label: 'Integrations',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: PLUGIN_LOTTIE_EFFECT,
      label: 'Lottie playback',
      config: {inOutLabel: null, directions: null},
    },
  ],

  [QuickEffectCategory.appear]: [
    {
      header: true,
      id: QuickEffectCategory.appear,
      label: 'Appear & Disappear',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: FADE_EFFECT,
      label: 'Fade',
      config: {inOutLabel: 'Fade', directions: null},
    },
    {
      header: false,
      id: SLIDE_EFFECT,
      label: 'Slide',
      config: {
        inOutLabel: 'Slide',
        directions: {
          in: getReverseDirection({...PATH_ICONS, ...CORNER_ICONS}),
          out: {...PATH_ICONS, ...CORNER_ICONS},
        },
      },
    },
    {
      header: false,
      id: FLIP_EFFECT,
      label: 'Flip',
      config: {
        inOutLabel: 'Flip',
        directions: {
          in: getReverseDirection(PATH_ICONS),
          out: PATH_ICONS,
        },
      },
    },
    {
      header: false,
      id: GROW_EFFECT,
      label: 'Grow',
      config: {inOutLabel: 'Grow', directions: null},
    },
    {
      header: false,
      id: GROW_BIG_EFFECT,
      label: 'Grow big',
      config: {inOutLabel: 'Grow', directions: null},
    },
    {
      header: false,
      id: SHRINK_EFFECT,
      label: 'Shrink',
      config: {inOutLabel: 'Shrink', directions: null},
    },
    {
      header: false,
      id: SHRINK_BIG_EFFECT,
      label: 'Shrink big',
      config: {inOutLabel: 'Shrink', directions: null},
    },
    {
      header: false,
      id: SPIN_EFFECT,
      label: 'Spin',
      config: {
        inOutLabel: 'Spin',
        directions: {
          in: getReverseDirection(ROTATE_ICONS),
          out: ROTATE_ICONS,
        },
      },
    },
    {
      header: false,
      id: FLY_EFFECT,
      label: 'Fly',
      config: {
        inOutLabel: 'Fly',
        directions: {
          in: getReverseDirection({...PATH_ICONS, ...CORNER_ICONS}),
          out: {...PATH_ICONS, ...CORNER_ICONS},
        },
      },
    },
    {
      header: false,
      id: DROP_EFFECT,
      label: 'Drop',
      config: {inOutLabel: 'Drop', directions: null},
    },
  ],

  [QuickEffectCategory.emphasis]: [
    {
      header: true,
      id: QuickEffectCategory.emphasis,
      label: 'Emphasis',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: POP_EFFECT,
      label: 'Pop',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: JIGGLE_EFFECT,
      label: 'Jiggle',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: PULSE_EFFECT,
      label: 'Pulse',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: BLINK_EFFECT,
      label: 'Blink',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: BOUNCE_EFFECT,
      label: 'Bounce',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: FLIP_LEFT_TO_RIGHT_EFFECT,
      label: 'Flip left to right',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: FLIP_RIGHT_TO_LEFT_EFFECT,
      label: 'Flip right to left',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: RUBBER_BAND_EFFECT,
      label: 'Rubber band',
      config: {inOutLabel: null, directions: null},
    },
    {
      header: false,
      id: JELLO_EFFECT,
      label: 'Jello',
      config: {inOutLabel: null, directions: null},
    },
  ],
} as const;

export const QuickEffectLabelMap = Object.keys(QuickEffectConsts).reduce<
  Record<string, any>
>((result, category) => {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly plugin: readonly [{ readonly header: true; readonly id: "plugin"; readonly label: "Integrations"; readonly config: { readonly inOutLabel: null; readonly directions: null; }; }, { readonly header: false; readonly id: "PLUGIN_LOTTIE_EFFECT"; readonly label: "Lottie playback"; readonly config: { ...; }; }]; ...'. | TS7031 - Binding element 'label' implicitly has an 'any' type. | TS7031 - Binding element 'header' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type.
  QuickEffectConsts[category].forEach(({label, header, id}) => {
    if (!header) {
      result[id] = label;
    }
  });

  return result;
}, {});

export const QuickEffectConfigMap = Object.keys(QuickEffectConsts).reduce<
  Record<string, any>
>((result, category) => {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly plugin: readonly [{ readonly header: true; readonly id: "plugin"; readonly label: "Integrations"; readonly config: { readonly inOutLabel: null; readonly directions: null; }; }, { readonly header: false; readonly id: "PLUGIN_LOTTIE_EFFECT"; readonly label: "Lottie playback"; readonly config: { ...; }; }]; ...'. | TS7031 - Binding element 'config' implicitly has an 'any' type. | TS7031 - Binding element 'header' implicitly has an 'any' type. | TS7031 - Binding element 'id' implicitly has an 'any' type.
  QuickEffectConsts[category].forEach(({config, header, id}) => {
    if (!header) {
      result[id] = config;
    }
  });

  return result;
}, {});
